import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, TicketPlus } from "lucide-react";
import { localApi } from "../lib/localApi";
import { NeoAvatar } from "./NeoAvatar";

const SUGGESTIONS = ["Combien coûte Casanéo pour 12 logements ?", "Quelles plateformes sont synchronisées ?", "Comment sont encaissées les cautions ?", "Y a-t-il une application mobile ?"];
const sid = () => { const k = "cn_support_sid"; let v = sessionStorage.getItem(k); if (!v) { v = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem(k, v); } return v; };

const LINK_LABELS = { "/demo": "Voir la démo", "/onboarding": "Essai gratuit", "/tarifs": "Tarifs", "/contact": "Contact", "/guides": "Guides", "/proprietaire-direct": "Offre propriétaires", "/fonctionnalites": "Fonctionnalités", "/blog": "Blog" };

/** Page /contact : Néo répond aux questions fréquentes ; « Créer un ticket » pré-remplit le formulaire avec la conversation. */
export function SupportChat({ onCreateTicket, page = "/contact" }) {
  const [messages, setMessages] = useState([{ role: "assistant", text: "Bonjour ! Je suis Néo, l'assistant IA de Casanéo. Posez-moi vos questions sur les fonctionnalités, les tarifs, les plateformes ou le démarrage — et si un humain est nécessaire, je vous aide à créer un ticket." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsHuman, setNeedsHuman] = useState(null);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [messages, busy]);
  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", text: q }]);
    try {
      const { data } = await localApi.post("/public/support-chat", { session_id: sid(), message: q, page });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, links: data.links }]);
      if (data.needs_human) setNeedsHuman(data.department || "contact");
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: e?.response?.data?.detail || "Je n'ai pas pu répondre — créez un ticket ci-dessous.", error: true }]);
      setNeedsHuman("contact");
    } finally { setBusy(false); }
  };
  const ticket = () => {
    const transcript = messages.slice(1).map((m) => `${m.role === "user" ? "Moi" : "Néo"} : ${m.text}`).join("\n");
    onCreateTicket?.({ subject: needsHuman || "contact", message: `Suite à ma conversation avec Néo :\n\n${transcript}\n\n` });
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] overflow-hidden" data-testid="support-chat">
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2"><NeoAvatar size={28} /><p className="text-sm font-medium text-white">Une question ? Néo répond tout de suite</p></div>
      <div className="px-4 py-4 space-y-3 max-h-80 overflow-y-auto" data-testid="support-chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 items-end ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role !== "user" && <NeoAvatar size={24} className="mb-0.5" />}
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-brass text-[#071e42]" : m.error ? "bg-rose-900/40 text-rose-100" : "bg-[#0b1a35] text-slate-100 border border-white/10"}`} data-testid={`support-chat-msg-${m.role}`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              {m.links?.length ? <p className="mt-2 flex flex-wrap gap-2">{m.links.map((l) => <Link key={l} to={l} className="text-xs text-brass underline">{LINK_LABELS[l] || l.replace(/^\//, "").replace(/[-/]/g, " ")}</Link>)}</p> : null}
            </div>
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Néo réfléchit…</div>}
        <div ref={endRef} />
      </div>
      {messages.length === 1 && (
        <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid="support-chat-suggestions">
          {SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => send(s)} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300 hover:border-brass/50 hover:text-white transition-colors" data-testid="support-chat-suggestion">{s}</button>)}
        </div>
      )}
      {needsHuman && (
        <div className="px-4 pb-3">
          <button type="button" onClick={ticket} className="w-full rounded-xl border border-brass/40 bg-brass/10 px-4 py-2.5 text-sm text-white inline-flex items-center justify-center gap-2 hover:bg-brass/20 transition-colors" data-testid="support-chat-ticket">
            <TicketPlus className="h-4 w-4 text-brass" /> Créer un ticket avec cette conversation
          </button>
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="px-4 py-3 border-t border-white/10 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Votre question…" maxLength={1500}
          className="flex-1 rounded-full bg-[#071e42] border border-white/15 px-4 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-brass" data-testid="support-chat-input" />
        <button type="submit" disabled={busy || !input.trim()} className="h-9 w-9 rounded-full bg-brass text-[#071e42] grid place-items-center disabled:opacity-50" data-testid="support-chat-send"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}
